import { create } from "zustand";

/**
 * Workbench-wide undo/redo infrastructure.
 *
 * A scope-keyed snapshot stack. Each surface (e.g. High-Lift) calls
 * registerScope on mount, which sets it as the active scope; the global
 * Cmd/Ctrl+Z controls and the top-bar buttons then act on whatever scope is
 * active. Snapshots are opaque — the store knows nothing about foil design or
 * any other domain; consumers structuredClone-restore whatever they pushed.
 *
 * Undo/redo do NOT mutate consumer state directly. They move the snapshot
 * between the past/future stacks and stage a pendingRestore; the owning surface
 * watches pendingRestore (by scope + nonce) and applies it via consumeRestore.
 */

export interface UndoRedoApi {
  /** The scope of the surface currently in focus; controls' default target. */
  activeScope: string | null;
  /** Surface calls on mount -> becomes the active scope. */
  registerScope(scope: string): void;
  /** Surface calls on unmount -> clears its history + activeScope if it was active. */
  unregisterScope(scope: string): void;
  /**
   * Append a snapshot; clears the redo/future stack for the scope.
   * Coalescing: if the previous push to this scope happened < coalesceMs ago,
   * REPLACE it instead of appending (so a drag = a single undo step).
   */
  push(scope: string, snapshot: unknown, opts?: { maxSize?: number; coalesceMs?: number }): void;
  /** Replace both stacks from a validated domain journal without staging a restore. */
  hydrate(scope: string, snapshots: { past: readonly unknown[]; future: readonly unknown[] }): void;
  /** Move current -> future and stage the prior snapshot as pendingRestore. */
  undo(scope?: string): void;
  /** Move a future snapshot back to the past and stage it as pendingRestore. */
  redo(scope?: string): void;
  canUndo(scope?: string): boolean;
  canRedo(scope?: string): boolean;
  /** Wipe a scope's history (past + future) without touching activeScope. */
  clear(scope: string): void;
  /** Set by undo/redo for the owning surface to pick up. */
  pendingRestore: { scope: string; snapshot: unknown; nonce: number } | null;
  /**
   * If pendingRestore is for this scope, return its snapshot and clear it;
   * otherwise return null.
   */
  consumeRestore(scope: string): unknown | null;
}

const DEFAULT_MAX_SIZE = 100;

/** Per-scope stack: `past` is the history (last entry = current), `future` = redo. */
interface ScopeHistory {
  past: unknown[];
  future: unknown[];
  /** Timestamp (ms) of the most recent push, for coalescing. */
  lastPushAt: number;
}

interface UndoRedoState extends UndoRedoApi {
  scopes: Record<string, ScopeHistory>;
}

function emptyHistory(): ScopeHistory {
  return { past: [], future: [], lastPushAt: 0 };
}

/** structuredClone with a defensive fallback for environments missing it. */
function cloneSnapshot(snapshot: unknown): unknown {
  if (typeof structuredClone === "function") {
    try {
      return structuredClone(snapshot);
    } catch {
      // Non-cloneable (functions, DOM nodes, …) — fall through to JSON.
    }
  }
  try {
    return JSON.parse(JSON.stringify(snapshot));
  } catch {
    return snapshot;
  }
}

export const useUndoRedoStore = create<UndoRedoState>((set, get) => ({
  activeScope: null,
  pendingRestore: null,
  scopes: {},

  registerScope(scope) {
    set((state) => ({
      activeScope: scope,
      scopes: state.scopes[scope] ? state.scopes : { ...state.scopes, [scope]: emptyHistory() },
    }));
  },

  unregisterScope(scope) {
    set((state) => {
      const nextScopes = { ...state.scopes };
      delete nextScopes[scope];
      return {
        scopes: nextScopes,
        activeScope: state.activeScope === scope ? null : state.activeScope,
        pendingRestore: state.pendingRestore?.scope === scope ? null : state.pendingRestore,
      };
    });
  },

  push(scope, snapshot, opts) {
    const maxSize = opts?.maxSize ?? DEFAULT_MAX_SIZE;
    const coalesceMs = opts?.coalesceMs ?? 0;
    const now = Date.now();
    set((state) => {
      const history = state.scopes[scope] ?? emptyHistory();
      const cloned = cloneSnapshot(snapshot);
      const coalesce = coalesceMs > 0 && history.past.length > 0 && now - history.lastPushAt < coalesceMs;

      let past: unknown[];
      if (coalesce) {
        // Replace the most recent entry so a continuous gesture = one undo step.
        past = history.past.slice(0, -1);
        past.push(cloned);
      } else {
        past = [...history.past, cloned];
      }
      // Cap the history; drop oldest entries past maxSize.
      if (past.length > maxSize) past = past.slice(past.length - maxSize);

      return {
        scopes: {
          ...state.scopes,
          // Any new push invalidates the redo stack.
          [scope]: { past, future: [], lastPushAt: now },
        },
      };
    });
  },

  hydrate(scope, snapshots) {
    set((state) => ({
      scopes: {
        ...state.scopes,
        [scope]: {
          past: snapshots.past.map(cloneSnapshot),
          future: snapshots.future.map(cloneSnapshot),
          lastPushAt: 0,
        },
      },
      pendingRestore: state.pendingRestore?.scope === scope ? null : state.pendingRestore,
    }));
  },

  undo(scope) {
    const target = scope ?? get().activeScope;
    if (!target) return;
    set((state) => {
      const history = state.scopes[target];
      if (!history || history.past.length < 2) return state;
      const past = history.past.slice();
      const current = past.pop() as unknown;
      const prior = past[past.length - 1];
      return {
        scopes: {
          ...state.scopes,
          [target]: { ...history, past, future: [current, ...history.future] },
        },
        pendingRestore: { scope: target, snapshot: prior, nonce: (state.pendingRestore?.nonce ?? 0) + 1 },
      };
    });
  },

  redo(scope) {
    const target = scope ?? get().activeScope;
    if (!target) return;
    set((state) => {
      const history = state.scopes[target];
      if (!history || history.future.length === 0) return state;
      const future = history.future.slice();
      const next = future.shift() as unknown;
      return {
        scopes: {
          ...state.scopes,
          [target]: { ...history, past: [...history.past, next], future },
        },
        pendingRestore: { scope: target, snapshot: next, nonce: (state.pendingRestore?.nonce ?? 0) + 1 },
      };
    });
  },

  canUndo(scope) {
    const target = scope ?? get().activeScope;
    if (!target) return false;
    const history = get().scopes[target];
    // Need at least 2 entries: the current snapshot and a prior to restore.
    return !!history && history.past.length >= 2;
  },

  canRedo(scope) {
    const target = scope ?? get().activeScope;
    if (!target) return false;
    const history = get().scopes[target];
    return !!history && history.future.length > 0;
  },

  clear(scope) {
    set((state) => {
      if (!state.scopes[scope]) return state;
      return {
        scopes: { ...state.scopes, [scope]: emptyHistory() },
        pendingRestore: state.pendingRestore?.scope === scope ? null : state.pendingRestore,
      };
    });
  },

  consumeRestore(scope) {
    const pending = get().pendingRestore;
    if (!pending || pending.scope !== scope) return null;
    set({ pendingRestore: null });
    return pending.snapshot;
  },
}));
